import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Download } from "lucide-react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";

import DashboardLayout from "./DashboardLayout";
import { clearAuthUser, computeReferralUrl, getAuthUser, saveAuthUser } from "../utils/auth";
import { readStoredTier } from "../utils/packageTier";
import { apiFetch } from "../utils/apiClient";

const defaultStats = [
  { label: "Clicks", value: 50 },
  { label: "Sign Up", value: 150 },
  { label: "Purchase", value: 50 },
  { label: "Commission", value: "100$" },
];

const defaultReferralStatsState = {
  clicks: 0,
  signups: 0,
  purchases: 0,
  commission: 0,
};

const resolveUserTier = (user) => {
  const userId = user?.id;
  const tierFromUser = Number(user?.packageTier);
  const storedTier = readStoredTier(userId);
  const tier = Number.isFinite(tierFromUser) ? tierFromUser : storedTier ?? 0;
  return Number.isFinite(tier) ? tier : 0;
};


export default function ReferrerLink() {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getAuthUser());
  const [copied, setCopied] = useState(false);
  const [referralStats, setReferralStats] = useState(
    user?.referralStats || defaultReferralStatsState,
  );
  const qrCodeRef = useRef(null);
  const [showCommunityModal, setShowCommunityModal] = useState(
    () => resolveUserTier(getAuthUser()) < 2,
  );

  const referralUrl = useMemo(() => {
    const url = computeReferralUrl(user);
    // Only return valid referral URL, not fallback
    if (url && typeof url === "string" && url.trim() && url.includes("/join/")) {
      return url.trim();
    }
    return null;
  }, [user]);

  // QR code should point to homepage instead of referral URL
  const qrTargetUrl = useMemo(() => {
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}/`;
    }
    return "https://synergycoinx.com/";
  }, []);

  // Gate: require package tier >= 2 (e.g., $200 or $500) to unlock Referrer page
  useEffect(() => {
    const tier = resolveUserTier(user);
    setShowCommunityModal(tier < 2);
  }, [user]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (showCommunityModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showCommunityModal]);

  useEffect(() => {
    if (user?.referralStats) {
      setReferralStats(user.referralStats);
    }
  }, [user]);

  useEffect(() => {
    let ignore = false;
    let intervalId;

    const fetchLatestStats = async () => {
      try {
        const response = await apiFetch("/users/profile");
        if (!ignore && response?.user) {
          saveAuthUser(response.user);
          setUser(response.user);
          setReferralStats(response.user.referralStats || defaultReferralStatsState);
        }
      } catch (error) {
        // ignore
      }
    };

    fetchLatestStats();
    intervalId = window.setInterval(fetchLatestStats, 15000);

    return () => {
      ignore = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = [
    {
      label: "Clicks",
      value: referralStats.clicks?.toLocaleString() ?? defaultStats[0].value,
    },
    {
      label: "Sign Ups",
      value: referralStats.signups?.toLocaleString() ?? defaultStats[1].value,
    },
    {
      label: "Purchases",
      value: referralStats.purchases?.toLocaleString() ?? defaultStats[2].value,
    },
    {
      label: "Commission",
      value:
        typeof referralStats.commission === "number"
          ? `$${referralStats.commission.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : defaultStats[3].value,
    },
  ];

  const handleSignOut = () => {
    clearAuthUser();
    navigate("/login", { replace: true });
  };

  const handleCopy = async () => {
    if (!referralUrl) {
      alert("No referral link available to copy");
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(referralUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = referralUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        } catch (err) {
          console.error("Failed to copy", err);
          alert("Failed to copy referral link. Please copy it manually.");
        }
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error("Failed to copy referral link", error);
      alert("Failed to copy referral link. Please try again.");
    }
  };

  const handleDownloadQr = () => {
    if (!referralUrl) {
      alert("No referral link available to download");
      return;
    }

    try {
      // Find the canvas element in the hidden div
      const canvas = qrCodeRef.current?.querySelector('canvas');
      if (!canvas) {
        console.warn("QR code canvas not found, creating temporary canvas");
        // Fallback: create a temporary canvas
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = 200;
        tempCanvas.height = 200;
        const ctx = tempCanvas.getContext('2d');
        if (!ctx) {
          alert("Failed to create QR code image");
          return;
        }
        // Draw white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, 200, 200);
        // For now, just download the SVG as fallback
        const svg = qrCodeRef.current?.querySelector('svg');
        if (svg) {
          const svgData = new XMLSerializer().serializeToString(svg);
          const blob = new Blob([svgData], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "synergycoin-referral-qr.svg";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
        return;
      }

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error("Failed to create blob from canvas");
          alert("Failed to create QR code image");
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "synergycoin-referral-qr.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }, 'image/png');
    } catch (error) {
      console.error("Failed to download QR code", error);
      alert("Failed to download QR code. Please try again.");
    }
  };

  return (
    <DashboardLayout user={user} onSignOut={handleSignOut} activeKey="referrer">
      {({ displayName }) => (
        <div className="relative">
          <header
            className={`relative ${
              showCommunityModal ? "z-[60] pointer-events-none" : "z-10"
            }`}
          >
            <div className="mb-6 text-sm text-white/50">
              Dashboard <span className="mx-2 text-white/30">›</span> Referrer Link
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Referrer Link
            </h1>
          </header>

          {/* Community Member Modal Card - Mobile only, below header */}
          {showCommunityModal && (
            <div className="mt-6 mb-6 md:hidden">
              <div className="relative w-full max-w-2xl mx-auto rounded-[20px] sm:rounded-[28px] bg-gradient-to-br from-[#1A0F2E] via-[#0F0A1A] to-[#050308] p-5 sm:p-6 md:p-7 shadow-[0_32px_90px_rgba(10,10,30,0.65)] border border-white/5">
                <div className="flex flex-col items-center text-center">
                  {/* Mobile Image */}
                  <div className="mb-3 sm:mb-4 md:mb-5">
                    <img 
                      src="/images/mobile.png" 
                      alt="Community Mobile" 
                      className="h-20 w-auto sm:h-28 md:h-32"
                    />
                  </div>
                  <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white leading-tight">
                  Refer & Earn Coins
                  </h2>
                  <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/70">
                  Share your referral link and earn coins every time someone joins or makes a purchase through you.
                  </p>
                  <p className="mt-1 text-base sm:text-lg md:text-xl font-bold text-[#A855F7]">
                  Invite. Refer. Earn more.
                  </p>
                </div>
                <div className="mt-4 sm:mt-5 md:mt-6 space-y-2 sm:space-y-3 text-xs sm:text-sm text-white/80">
                  <p className="font-bold text-white text-sm sm:text-base">Referrer Incentives</p>
                  <ul className="list-disc space-y-1.5 sm:space-y-2 pl-4 sm:pl-5 md:pl-6 text-left text-white/80">
                    <li className="text-xs sm:text-sm leading-relaxed">Receive your unique referral link and QR code</li>
                    <li className="text-xs sm:text-sm leading-relaxed">Earn coins for every successful referral signup</li>
                    <li className="text-xs sm:text-sm leading-relaxed">
                    Receive coin bonuses when referrals purchase coin notes
                    </li>
                    <li className="text-xs sm:text-sm leading-relaxed">Each coin note purchase has a minimum value of $25</li>
                    <li className="text-xs sm:text-sm leading-relaxed">Get bonus coins per coin note purchase</li>
                    <li className="text-xs sm:text-sm leading-relaxed">No limit on referrals or earnings</li>

                  </ul>
                </div>
                <div className="mt-4 sm:mt-5 md:mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCommunityModal(false);
                      navigate("/packages");
                    }}
                    className="w-full rounded-lg sm:rounded-xl bg-gradient-to-r from-[#8C3CDD] to-[#A367FF] py-3 sm:py-3.5 md:py-4 text-sm sm:text-base font-bold text-white shadow-[0_8px_24px_rgba(140,60,221,0.4)] transition hover:shadow-[0_12px_32px_rgba(140,60,221,0.6)] hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Start Referring
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={`mt-6 space-y-8 ${showCommunityModal ? "blur-[2px] opacity-90 pointer-events-none" : ""}`}>
            <section>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex w-full items-center gap-3 rounded-full border border-white/10 bg-[#1A1124] px-5 py-3">
                  <span className="truncate text-sm text-white/80">
                    {referralUrl || "No referral link available"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={!referralUrl}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#8C3CDD] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#A367FF] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Copy size={16} />
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              {copied && referralUrl && (
                <p className="text-xs text-emerald-300">Referral link copied to clipboard.</p>
              )}
              {!referralUrl && (
                <p className="text-xs text-yellow-300">Please wait for referral link to load...</p>
              )}
            </section>

            <section className="rounded-3xl border border-white/10 bg-gradient-to-r from-[#31155A] via-[#2A1A78] to-[#132355] p-4 text-white shadow-[0_18px_60px_rgba(52,27,117,0.45)] sm:p-6">
              <div className="grid gap-5 md:gap-6 lg:grid-cols-[2fr,auto] lg:items-center">
                <div className="space-y-3 sm:space-y-4">
                  <p className="text-lg font-semibold text-white sm:text-xl">
                    Scan To Connect with SynergyCoin
                  </p>
                  {/* hsh */}
                  <p className="text-xs text-white/75 sm:text-sm">
                    Connect instantly to explore exclusive rewards, affiliate insights, and growth
                    opportunities. Stay updated with real-time stats and begin your earning journey
                    today.
                  </p>
                  <button
                    type="button"
                    onClick={handleDownloadQr}
                    className="inline-flex items-center gap-2 rounded-md bg:white/10 px-4 py-2 text-xs font-semibold text-white transition hover:bg-white/15 sm:px-5 sm:py-2.5 sm:text-sm"
                  >
                    <Download size={16} />
                    Download Qr Code
                  </button>
                </div>

                <div className="flex items-center justify-center mt-4 md:mt-0">
                  <div 
                    ref={qrCodeRef}
                    className="flex items-center justify-center rounded-2xl border border-white/20 bg-white p-3 shadow-[0_12px_40px_rgba(0,0,0,0.35)] sm:rounded-3xl sm:p-4"
                  >
                    {referralUrl ? (
                      <div className="relative">
                        {/* Hidden canvas for download */}
                        <div className="absolute opacity-0 pointer-events-none" style={{ left: '-9999px' }}>
                          <QRCodeCanvas
                            value={qrTargetUrl}
                            size={170}
                            level="H"
                            includeMargin={true}
                          />
                        </div>
                        {/* Visible SVG for display */}
                        <QRCodeSVG
                          value={qrTargetUrl}
                          size={170}
                          level="H"
                          includeMargin={true}
                          className="rounded-2xl"
                          bgColor="#FFFFFF"
                          fgColor="#000000"
                        />
                      </div>
                    ) : (
                      <div className="h-[200px] w-[200px] rounded-2xl bg-gray-200 flex items-center justify-center">
                        <span className="text-xs text-gray-500 text-center px-2">No referral link available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-2xl font-bold text-white">Overview</h2>
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white/80 transition hover:bg-white/10"
                  title="Download CVS"
                >
                  Download CVS
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl border border-[#3B1A63] bg-[#25093F] px-6 py-5 shadow-[0_14px_42px_rgba(62,30,120,0.3)]"
                  >
                    <p className="text-sm text-white/60">{stat.label}</p>
                    <p className="mt-3 text-xl font-semibold text-white">{stat.value}</p>
                  </div>
                ))}
              </div>
            </section>

            <p className="text-xs text-white/40">
              Sharing your referral link helps others discover SynergyCoin. Track performance stats
              in real time and export the data whenever you need.
            </p>
          </div>
              <div className="pb-24 sm:pb-0"></div>

          {/* Community Member Modal - Desktop only */}
          {showCommunityModal && (
            <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center bg-black/30 backdrop-blur-sm px-4 pt-6 pb-6 sm:py-8 md:pt-24 md:left-[280px] overflow-y-auto">
              <div className="relative w-full max-w-lg rounded-[20px] sm:rounded-[28px] bg-gradient-to-br from-[#1A0F2E] via-[#0F0A1A] to-[#050308] p-5 sm:p-6 md:p-7 shadow-[0_32px_90px_rgba(10,10,30,0.65)] border border-white/5">
                <div className="flex flex-col items-center text-center">
                  {/* Mobile Image */}
                  <div className="mb-3 sm:mb-4 md:mb-5">
                    <img 
                      src="/images/mobile.png" 
                      alt="Community Mobile" 
                      className="h-20 w-auto sm:h-28 md:h-32"
                    />
                  </div>
                  <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white leading-tight">
                  Refer & Earn Coins
                  </h2>
                  <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/70">
                  Share your referral link and earn coins every time someone joins or makes a purchase through you.
                  </p>
                  <p className="mt-1 text-base sm:text-lg md:text-xl font-bold text-[#A855F7]">
                  Invite. Refer. Earn more.
                  </p>
                </div>
                <div className="mt-4 sm:mt-5 md:mt-6 space-y-2 sm:space-y-3 text-xs sm:text-sm text-white/80">
                  <p className="font-bold text-white text-sm sm:text-base">Referrer Incentives</p>
                  <ul className="list-disc space-y-1.5 sm:space-y-2 pl-4 sm:pl-5 md:pl-6 text-left text-white/80">
                    <li className="text-xs sm:text-sm leading-relaxed">Receive your unique referral link and QR code</li>
                    <li className="text-xs sm:text-sm leading-relaxed">Earn coins for every successful referral signup</li>
                    <li className="text-xs sm:text-sm leading-relaxed">
                    Receive coin bonuses when referrals purchase coin notes
                    </li>
                    <li className="text-xs sm:text-sm leading-relaxed">Each coin note purchase has a minimum value of $25</li>
                    <li className="text-xs sm:text-sm leading-relaxed">Get bonus coins per coin note purchase</li>
                    <li className="text-xs sm:text-sm leading-relaxed">No limit on referrals or earnings</li>

                  </ul>
                </div>
                <div className="mt-4 sm:mt-5 md:mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCommunityModal(false);
                      navigate("/packages");
                    }}
                    className="w-full rounded-lg sm:rounded-xl bg-gradient-to-r from-[#8C3CDD] to-[#A367FF] py-3 sm:py-3.5 md:py-4 text-sm sm:text-base font-bold text-white shadow-[0_8px_24px_rgba(140,60,221,0.4)] transition hover:shadow-[0_12px_32px_rgba(140,60,221,0.6)] hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Start Referring
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
